import { Loader2, WifiOff } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { AtemPanel, type AtemCommand } from "./AtemPanel";
import type { ConnectionStatus } from "@/hooks/useBridgeConnections";
import type { DeviceStateStore } from "@production-hub/shared";
import { storeToAtemState } from "@production-hub/shared";

interface AtemControlProps {
  bridgeUrl?: string;
  store?: DeviceStateStore;
  connectionId: string;
  isLoading?: boolean;
  isError?: boolean;
  connectionStatus?: ConnectionStatus;
  onUpdateValue?: (
    connectionId: string,
    path: string,
    value: string | number | boolean,
  ) => void;
}

/**
 * Maps AtemCommand actions to OSC-style path updates.
 */
function commandToPathUpdates(
  cmd: AtemCommand,
): Array<{ path: string; value: string | number | boolean }> {
  switch (cmd.action) {
    case "label_input":
      return [
        { path: `/inputs/${cmd.index}/shortName`, value: cmd.shortName },
        { path: `/inputs/${cmd.index}/longName`, value: cmd.longName },
      ];
    case "set_program":
      return [
        {
          path: `/mixEffects/${cmd.meIndex}/programInput`,
          value: cmd.inputIndex,
        },
      ];
    case "set_preview":
      return [
        {
          path: `/mixEffects/${cmd.meIndex}/previewInput`,
          value: cmd.inputIndex,
        },
      ];
    case "cut":
    case "auto":
    case "fade_to_black":
      return [];
    case "set_transition_style":
      return [
        {
          path: `/mixEffects/${cmd.meIndex}/transition/style`,
          value: cmd.style,
        },
      ];
    case "set_transition_rate":
      return [
        { path: `/mixEffects/${cmd.meIndex}/transition/rate`, value: cmd.rate },
      ];
    case "set_ftb_rate":
      return [
        { path: `/mixEffects/${cmd.meIndex}/fadeToBlackRate`, value: cmd.rate },
      ];
    case "set_aux":
      return [
        { path: `/auxiliaries/${cmd.auxIndex}/source`, value: cmd.inputIndex },
      ];
    case "label_aux":
      return [
        {
          path: `/auxiliaries/${cmd.auxIndex}/shortName`,
          value: cmd.shortName,
        },
        { path: `/auxiliaries/${cmd.auxIndex}/longName`, value: cmd.longName },
      ];
    case "label_fairlight_input":
      return [
        {
          path: `/audio/fairlightInputs/${cmd.inputIndex}/shortName`,
          value: cmd.shortName,
        },
        {
          path: `/audio/fairlightInputs/${cmd.inputIndex}/longName`,
          value: cmd.longName,
        },
      ];
    case "set_usk_on_air":
      return [
        {
          path: `/upstreamKeyers/${cmd.keyerIndex}/onAir`,
          value: cmd.onAir,
        },
      ];
    case "set_usk_fill_source":
      return [
        {
          path: `/upstreamKeyers/${cmd.keyerIndex}/fillSource`,
          value: cmd.source,
        },
      ];
    case "set_usk_key_source":
      return [
        {
          path: `/upstreamKeyers/${cmd.keyerIndex}/keySource`,
          value: cmd.source,
        },
      ];
    case "set_usk_type":
      return [
        { path: `/upstreamKeyers/${cmd.keyerIndex}/type`, value: cmd.type },
      ];
    case "set_dsk_on_air":
      return [
        { path: `/downstreamKeyers/${cmd.dskIndex}/onAir`, value: cmd.onAir },
      ];
    case "set_dsk_tie":
      return [
        { path: `/downstreamKeyers/${cmd.dskIndex}/tie`, value: cmd.tie },
      ];
    case "set_dsk_auto":
      return [];
    case "set_dsk_fill_source":
      return [
        {
          path: `/downstreamKeyers/${cmd.dskIndex}/fillSource`,
          value: cmd.source,
        },
      ];
    case "set_dsk_key_source":
      return [
        {
          path: `/downstreamKeyers/${cmd.dskIndex}/keySource`,
          value: cmd.source,
        },
      ];
    case "set_dsk_rate":
      return [
        { path: `/downstreamKeyers/${cmd.dskIndex}/rate`, value: cmd.rate },
      ];
    case "set_color_generator":
      return [
        { path: `/colorGenerators/${cmd.index}/hue`, value: cmd.hue },
        {
          path: `/colorGenerators/${cmd.index}/saturation`,
          value: cmd.saturation,
        },
        {
          path: `/colorGenerators/${cmd.index}/luminance`,
          value: cmd.luminance,
        },
      ];
    case "set_audio_mix_option":
      return [
        {
          path: `/audio/channels/${cmd.channelIndex}/mixOption`,
          value: cmd.mixOption,
        },
      ];
    case "set_audio_gain":
      return [
        { path: `/audio/channels/${cmd.channelIndex}/gain`, value: cmd.gain },
      ];
    case "set_audio_balance":
      return [
        {
          path: `/audio/channels/${cmd.channelIndex}/balance`,
          value: cmd.balance,
        },
      ];
    case "set_audio_master_gain":
      return [{ path: `/audio/master/gain`, value: cmd.gain }];
    case "set_media_player_source":
      return [
        {
          path: `/mediaPlayers/${cmd.mpIndex}/sourceType`,
          value: cmd.sourceType,
        },
        {
          path: `/mediaPlayers/${cmd.mpIndex}/sourceIndex`,
          value: cmd.sourceIndex,
        },
      ];
    case "set_media_player_playing":
      return [
        {
          path: `/mediaPlayers/${cmd.mpIndex}/playing`,
          value: cmd.playing,
        },
      ];
    case "set_media_player_loop":
      return [{ path: `/mediaPlayers/${cmd.mpIndex}/loop`, value: cmd.loop }];
    case "set_supersource_box": {
      const updates: Array<{
        path: string;
        value: string | number | boolean;
      }> = [];
      const bp = `/superSources/${cmd.ssIndex}/boxes/${cmd.boxIndex}`;
      const u = cmd.updates;
      if (u.enabled !== undefined)
        updates.push({ path: `${bp}/enabled`, value: u.enabled });
      if (u.source !== undefined)
        updates.push({ path: `${bp}/source`, value: u.source });
      if (u.x !== undefined) updates.push({ path: `${bp}/x`, value: u.x });
      if (u.y !== undefined) updates.push({ path: `${bp}/y`, value: u.y });
      if (u.size !== undefined)
        updates.push({ path: `${bp}/size`, value: u.size });
      if (u.cropped !== undefined)
        updates.push({ path: `${bp}/cropped`, value: u.cropped });
      if (u.cropTop !== undefined)
        updates.push({ path: `${bp}/cropTop`, value: u.cropTop });
      if (u.cropBottom !== undefined)
        updates.push({ path: `${bp}/cropBottom`, value: u.cropBottom });
      if (u.cropLeft !== undefined)
        updates.push({ path: `${bp}/cropLeft`, value: u.cropLeft });
      if (u.cropRight !== undefined)
        updates.push({ path: `${bp}/cropRight`, value: u.cropRight });
      return updates;
    }
    case "set_supersource_art":
      return [
        {
          path: `/superSources/${cmd.ssIndex}/artFillSource`,
          value: cmd.fillSource,
        },
        {
          path: `/superSources/${cmd.ssIndex}/artKeySource`,
          value: cmd.keySource,
        },
      ];
    case "set_multiview_window_source":
      return [
        {
          path: `/multiViews/${cmd.mvIndex}/windows/${cmd.windowIndex}/source`,
          value: cmd.source,
        },
      ];
    case "set_multiview_layout":
      return [{ path: `/multiViews/${cmd.mvIndex}/layout`, value: cmd.layout }];
    case "set_multiview_vu_meters":
      return [
        {
          path: `/multiViews/${cmd.mvIndex}/vuMetersEnabled`,
          value: cmd.enabled,
        },
      ];
    case "set_multiview_program_preview_swapped":
      return [
        {
          path: `/multiViews/${cmd.mvIndex}/programPreviewSwapped`,
          value: cmd.swapped,
        },
      ];
    case "set_audio_monitor_gain":
      return [{ path: `/audio/monitorGain`, value: cmd.gain }];
    case "set_audio_monitor_mute":
      return [{ path: `/audio/monitorMute`, value: cmd.mute }];
    case "set_audio_monitor_solo":
      return [
        { path: `/audio/monitorSolo`, value: cmd.solo },
        { path: `/audio/monitorSoloInput`, value: cmd.inputIndex },
      ];
    case "set_audio_headphone_gain":
      return [{ path: `/audio/headphoneGain`, value: cmd.gain }];
    case "set_audio_headphone_mute":
      return [{ path: `/audio/headphoneMute`, value: cmd.mute }];
    case "set_fairlight_input_gain":
      return [
        {
          path: `/audio/fairlightInputs/${cmd.inputIndex}/sources/${cmd.sourceId}/gain`,
          value: cmd.gain,
        },
      ];
    case "set_fairlight_input_fader":
      return [
        {
          path: `/audio/fairlightInputs/${cmd.inputIndex}/sources/${cmd.sourceId}/faderGain`,
          value: cmd.faderGain,
        },
      ];
    case "set_fairlight_input_balance":
      return [
        {
          path: `/audio/fairlightInputs/${cmd.inputIndex}/sources/${cmd.sourceId}/balance`,
          value: cmd.balance,
        },
      ];
    case "set_fairlight_input_mix_option":
      return [
        {
          path: `/audio/fairlightInputs/${cmd.inputIndex}/sources/${cmd.sourceId}/mixOption`,
          value: cmd.mixOption,
        },
      ];
    case "set_fairlight_input_configuration":
      return [
        {
          path: `/audio/fairlightInputs/${cmd.inputIndex}/activeConfiguration`,
          value: cmd.configuration,
        },
      ];
    case "set_audio_routing_channel_pair":
      return [
        {
          path: `/audio/routing/${cmd.outputIndex}/channelPairs/${cmd.pairIndex}/sourceInputIndex`,
          value: cmd.sourceInputIndex,
        },
        {
          path: `/audio/routing/${cmd.outputIndex}/channelPairs/${cmd.pairIndex}/sourceId`,
          value: cmd.sourceId,
        },
        {
          path: `/audio/routing/${cmd.outputIndex}/channelPairs/${cmd.pairIndex}/enabled`,
          value: cmd.enabled,
        },
      ];
    case "run_macro":
    case "stop_macro":
      return [];
    default:
      return [];
  }
}

export const AtemControl = ({
  store,
  connectionId,
  isLoading,
  isError,
  connectionStatus,
  onUpdateValue,
}: AtemControlProps) => {
  if (connectionStatus && connectionStatus !== "connected") {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <WifiOff className="h-8 w-8 mb-2" />
          <p>Device not connected</p>
          <p className="text-sm">Connect to view and control the ATEM</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-8 w-8 mb-2 animate-spin" />
          <p>Loading device state...</p>
        </CardContent>
      </Card>
    );
  }

  if (isError || !store) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-destructive">
          <WifiOff className="h-8 w-8 mb-2" />
          <p>Failed to load device state</p>
          <p className="text-sm text-muted-foreground">
            Check that the device is responding
          </p>
        </CardContent>
      </Card>
    );
  }

  const state = storeToAtemState(store);

  const handleCommand = (cmd: AtemCommand) => {
    const updates = commandToPathUpdates(cmd);
    for (const { path, value } of updates) {
      onUpdateValue?.(connectionId, path, value);
    }
  };

  return <AtemPanel state={state} onCommand={handleCommand} />;
};
